"use client";

import { useState, type ReactNode } from "react";

import {
    CaretLeftIcon,
    CaretRightIcon,
} from "@phosphor-icons/react/ssr";

import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
} from "~/components/ui/carousel";

import usePrevNextButtons from "~/componentsND/carousel/usePrevNextButtons";
import Paragraph from "~/componentsDS/Typography/Paragraph";

import { cn } from "~/lib/utils";
import Autoplay from "embla-carousel-autoplay";

export const PROMO_BAR_VARIANT = {
    CAROUSEL: "carousel",
    /** The first message only, clamped to two lines, then `link`, with no carousel or arrows. */
    STATIC: "static",
} as const;

type PromoBarVariant = typeof PROMO_BAR_VARIANT[keyof typeof PROMO_BAR_VARIANT];

type Props = {
    className?: string;
    messages?: ReactNode[];
    variant?: PromoBarVariant;
    /** Static variant only: shown after the message and never clamped with it. */
    link?: ReactNode;
};

const autoplay = Autoplay({ playOnInit: true, delay: 4000 });

const DEFAULT_MESSAGES = [
    "Prescribed by licensed physician",
    "Doctor visits included",
    "HSA/FSA eligible",
    "Money Back Guarantee on eligible products",
];

export const PromoBar = ({ className, messages = DEFAULT_MESSAGES, variant = PROMO_BAR_VARIANT.CAROUSEL, link }: Props) => {
    const [emblaApi, setApi] = useState<CarouselApi>();

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = usePrevNextButtons(emblaApi);

    if (variant === PROMO_BAR_VARIANT.STATIC) {
        return (
            <div className={cn("bg-primary500 text-pureWhite text-center p-[10px] flex items-center justify-center gap-2", className)}>
                <Paragraph size="sm" sizes={{ md: "md" }} className="leading-5 line-clamp-2">{messages[0]}</Paragraph>
                {link && (
                    <Paragraph as="span" size="sm" sizes={{ md: "md" }} className="leading-5 shrink-0 whitespace-nowrap">{link}</Paragraph>
                )}
            </div>
        );
    }

    return (
        <div className={cn("bg-primary500 text-pureWhite text-center p-[10px] flex gap-2 justify-center", className)}>
            <button disabled={prevBtnDisabled} onClick={onPrevButtonClick}>
                <CaretLeftIcon size={20} />
            </button>

            <Carousel setApi={setApi} plugins={[autoplay]} className="max-w-[360px]">
                <CarouselContent>
                    {messages.map((message, index) => {
                        return (
                            <CarouselItem key={index}>
                                <Paragraph size="sm" sizes={{ md: "md" }} className="leading-5 flex justify-center gap-2">{message}</Paragraph>
                            </CarouselItem>
                        );
                    })}
                </CarouselContent>
            </Carousel>

            <button disabled={nextBtnDisabled} onClick={onNextButtonClick}>
                <CaretRightIcon size={20} />
            </button>
        </div>
    );
};

export default PromoBar;
